import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import {
  AiMedicalDocumentItem,
  AiMedicalDocumentPayload,
  DocumentsContextSearchResponse,
  DocumentsSearchPayload,
  HealthManagerTagModel,
  MedicalDocumentItem,
  UploadDocumentResponse,
} from '@lib/medical-documents';
import { Observable, tap } from 'rxjs';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';

import { PagedResponse } from '@lib/core/models/paged-response';

import { MD_API_URL } from '../tokens/medical-documents.tokens';

@Injectable({
  providedIn: 'root',
})
export class MedicalDocumentsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(MD_API_URL);

  private healthManagerTagsCache: HealthManagerTagModel[] | null = null;

  fetchMemberDocumentsList(
    memberId: string,
    requestMetadata: {
      page: number;
      pageSize: number;
      searchTerm: string;
    }
  ): Observable<PagedResponse<MedicalDocumentItem>> {
    const options = {
      params: new HttpParams()
        .set('memberId', memberId)
        .set('pageSize', requestMetadata.pageSize || 50)
        .set('page', requestMetadata.page || 1)
        .set('searchTerm', requestMetadata.searchTerm || ''),
    };
    return this.http.get<PagedResponse<MedicalDocumentItem>>(`${this.apiUrl}/medical-records/documents/search`, options);
  }

  getDocumentsByUserId(userId: string) {
    return this.http.get<AiMedicalDocumentItem[]>(`${this.apiUrl}/${userId}/medical-documents`);
  }

  fetchMemberDocument(medicalRecordId: string, documentId: string) {
    return this.http.get(`${this.apiUrl}/medical-records/${medicalRecordId}/documents/${documentId}/file`, {
      responseType: 'arraybuffer',
    });
  }

  fetchAIMedicalDocument(userId: string, documentId: string) {
    return this.http.get(`${this.apiUrl}/${userId}/medical-documents/${documentId}/read`, {
      responseType: 'arraybuffer',
    });
  }

  searchMedicalRecords(payload: DocumentsSearchPayload) {
    const options = {
      params: new HttpParams()
        .set('memberId', payload.memberId)
        .set('page', payload.page ?? 1)
        .set('pageSize', payload.pageSize ?? DEFAULT_PAGE_SIZE)
        .set('searchTerm', payload.searchTerm),
    };

    return this.http.get<PagedResponse<MedicalDocumentItem>>(`${this.apiUrl}/medical-records/documents/search`, options);
  }

  contextSearchMedicalRecords(userId: string, searched: string) {
    return this.http.post<DocumentsContextSearchResponse>(`${this.apiUrl}/${userId}/medical-documents/completion`, { message: searched });
  }

  getMedicalRecordFile(actionId: string, recordId: string, documentId: string) {
    return this.http.get(`${this.apiUrl}/members/actions/${actionId}/medical-records/${recordId}/documents/${documentId}`, {
      responseType: 'arraybuffer',
    });
  }

  uploadMedicalDocument(userId: string, file: File): Observable<UploadDocumentResponse> {
    const formData = new FormData();
    formData.append('file', file as Blob);

    return this.http.post<UploadDocumentResponse>(`${this.apiUrl}/${userId}/medical-documents/upload`, formData);
  }

  updateMedicalDocumentDetails(userId: string, documentId: string, payload: AiMedicalDocumentPayload, ownershipConfirmed?: boolean) {
    const params = ownershipConfirmed !== undefined ? new HttpParams().set('ownershipConfirmed', ownershipConfirmed.toString()) : undefined;
    return this.http.put(`${this.apiUrl}/${userId}/medical-documents/${documentId}`, payload, { params });
  }


  deleteMedicalDocument(userId: string, documentId: string) {
    return this.http.delete(`${this.apiUrl}/${userId}/medical-documents/${documentId}`);
  }

  getHealthManagerTags(): Observable<HealthManagerTagModel[]> {
    if (this.healthManagerTagsCache) {
      return new Observable(subscriber => {
        subscriber.next(this.healthManagerTagsCache!);
        subscriber.complete();
      });
    }

    return this.http.get<HealthManagerTagModel[]>(`${this.apiUrl}/tags`).pipe(
      tap(tags => this.healthManagerTagsCache = tags)
    );
  }
}
